/**
 * RecurringTab — extracted from EmployeeCreate.js.
 * Receives state + handlers via `ctx` prop bag.
 */
import React from 'react';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Textarea } from '../../../../components/ui/textarea';
import { TabsContent } from '../../../../components/ui/tabs';
import { Badge } from '../../../../components/ui/badge';
import { Plus, Trash2, User, Building2, Briefcase, Mail, Phone, Home, CreditCard, FileText, GraduationCap, Clock, DollarSign, Users, Heart, Calendar, Wallet, RefreshCw } from 'lucide-react';

const RecurringTab = ({ ctx }) => {
  const {
    formData,
    handleAddRecurringPayment,
    handleRecurringPaymentChange,
    handleRemoveRecurringPayment,
  } = ctx;

  return (
          <TabsContent value="recurring">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-stone-700" />
                    Recurring Payments/Deductions (Infotype 0014)
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddRecurringPayment}>
                    <Plus className="w-4 h-4 mr-1" /> Add Entry
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {formData.recurring_payments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No recurring payments or deductions added yet.</p>
                    <p className="text-sm">Click "Add Entry" to add loan deductions, allowances, etc.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {formData.recurring_payments.map((payment, index) => (
                      <div key={payment._uid} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium text-gray-700">
                            {payment.payment_type === 'PAYMENT' ? 'Recurring Payment' : 'Recurring Deduction'} #{index + 1}
                          </h4>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveRecurringPayment(payment._uid)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Type *</Label>
                            <select
                              value={payment.payment_type}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'payment_type', e.target.value)}
                              className="w-full h-10 px-3 border rounded-md"
                            >
                              <option value="DEDUCTION">Deduction</option>
                              <option value="PAYMENT">Payment/Allowance</option>
                            </select>
                          </div>
                          <div>
                            <Label>Wage Type Code *</Label>
                            <Input
                              value={payment.wage_type}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'wage_type', e.target.value)}
                              placeholder="e.g., 1001, DED01"
                            />
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Input
                              value={payment.wage_type_name || ''}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'wage_type_name', e.target.value)}
                              placeholder="e.g., Car Loan, HRA"
                            />
                          </div>
                          <div>
                            <Label>Amount *</Label>
                            <Input
                              type="number"
                              value={payment.amount}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'amount', e.target.value ? parseFloat(e.target.value) : '')}
                              placeholder="5000"
                            />
                          </div>
                          <div>
                            <Label>Currency</Label>
                            <select
                              value={payment.currency}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'currency', e.target.value)}
                              className="w-full h-10 px-3 border rounded-md"
                            >
                              <option value="INR">INR</option>
                              <option value="USD">USD</option>
                              <option value="EUR">EUR</option>
                              <option value="GBP">GBP</option>
                            </select>
                          </div>
                          <div>
                            <Label>Frequency</Label>
                            <select
                              value={payment.frequency}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'frequency', e.target.value)}
                              className="w-full h-10 px-3 border rounded-md"
                            >
                              <option value="MONTHLY">Monthly</option>
                              <option value="WEEKLY">Weekly</option>
                              <option value="ONE_TIME">One Time</option>
                            </select>
                          </div>
                          <div>
                            <Label>Start Date *</Label>
                            <Input
                              type="date"
                              value={payment.start_date}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'start_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>End Date</Label>
                            <Input
                              type="date"
                              value={payment.end_date || ''}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'end_date', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Total Installments</Label>
                            <Input
                              type="number"
                              value={payment.number_of_installments || ''}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'number_of_installments', e.target.value ? parseInt(e.target.value) : '')}
                              placeholder="12"
                            />
                          </div>
                          <div className="col-span-3">
                            <Label>Remarks</Label>
                            <Input
                              value={payment.remarks || ''}
                              onChange={(e) => handleRecurringPaymentChange(payment._uid, 'remarks', e.target.value)}
                              placeholder="Additional notes..."
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
  );
};

export default RecurringTab;
